import { useState } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import ChatWidget from "../../components/feature/ChatWidget";
import SEOHead from "../../components/feature/SEOHead";
import { blogPosts, blogCategories } from "../../mocks/blog";
import { Link } from "react-router-dom";

const siteUrl = import.meta.env.VITE_SITE_URL ?? "https://vidalferrerassessors.es";

const blogJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Fiscal y Laboral — Vidal Ferrer Asesores",
    "description": "Artículos, guías y novedades sobre fiscalidad, legislación laboral y seguros escritos por los asesores de Vidal Ferrer en Pego.",
    "url": `${siteUrl}/blog`,
    "author": {
      "@type": "Organization",
      "name": "Vidal Ferrer Asesores",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "Vidal Ferrer Asesores",
      "url": siteUrl
    },
    "inLanguage": "es-ES"
  }
];

export default function BlogPage() {
  const [activeCategory, setActiveCategory] = useState("Todos");

  const filtered = activeCategory === "Todos" ? blogPosts : blogPosts.filter((p) => p.category === activeCategory);
  const featured = blogPosts.find((p) => p.featured);
  const rest = filtered.filter((p) => !p.featured);

  return (
    <>
      <SEOHead
        title="Blog Fiscal y Laboral · Novedades | Vidal Ferrer Asesores Pego"
        description="Artículos y guías sobre fiscalidad, legislación laboral y seguros escritos por los asesores de Vidal Ferrer en Pego. Actualizado regularmente."
        keywords="blog fiscal Pego, novedades fiscalidad España, artículos laborales Alicante, noticias asesoría Pego"
        canonical="/blog"
        ogType="website"
        jsonLd={blogJsonLd}
      />
      <Navbar />
      <main>
        {/* Hero */}
        <section className="pt-32 pb-16 bg-[#F0F4ED]">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10 text-center">
            <p className="text-[#6B7F5C] text-sm font-semibold uppercase tracking-widest mb-4">Novedades</p>
            <h1 className="text-4xl md:text-6xl font-bold text-[#1A1A1A] mb-5" style={{ fontFamily: "'Playfair Display', serif" }}>
              Blog y Actualidad
            </h1>
            <p className="text-[#6B6B6B] text-lg max-w-2xl mx-auto">
              Artículos, guías y novedades sobre fiscalidad, legislación laboral y seguros escritos por nuestros expertos.
            </p>
          </div>
        </section>

        <section className="py-16 bg-white">
          <div className="max-w-screen-xl mx-auto px-6 md:px-10">
            {/* Featured post */}
            {featured && activeCategory === "Todos" && (
              <div className="bg-[#F8F7F4] rounded-2xl overflow-hidden mb-12 grid md:grid-cols-5 group">
                <div className="md:col-span-2 h-64 md:h-auto overflow-hidden">
                  <img
                    src={featured.image}
                    alt={featured.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="bg-[#6B7F5C] text-white text-xs font-semibold px-3 py-1 rounded-full">Destacado</span>
                    <span className="bg-[#F0F4ED] text-[#6B7F5C] text-xs font-medium px-2.5 py-1 rounded-full">{featured.category}</span>
                    <span className="text-[#6B6B6B] text-xs">{featured.date}</span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {featured.title}
                  </h2>
                  <p className="text-[#6B6B6B] leading-relaxed mb-6">{featured.excerpt}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-sm text-[#6B6B6B]">
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-user-line" />
                      </div>
                      {featured.author}
                      <span>·</span>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-time-line" />
                      </div>
                      {featured.readTime} lectura
                    </div>
                    <Link
                      to="/blog"
                      className="flex items-center gap-1 text-[#6B7F5C] text-sm font-medium hover:gap-2 transition-all cursor-pointer"
                    >
                      Leer artículo
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-arrow-right-line" />
                      </div>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            {/* Category filter */}
            <div className="flex flex-wrap gap-3 mb-10">
              {blogCategories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveCategory(cat)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === cat
                      ? "bg-[#6B7F5C] text-white"
                      : "bg-[#F0F4ED] text-[#4A4A4A] hover:bg-[#6B7F5C]/20"
                  }`}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Posts grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {rest.map((post) => (
                <article key={post.id} className="bg-[#F8F7F4] rounded-2xl overflow-hidden group hover:-translate-y-1 transition-all duration-300">
                  <div className="h-48 overflow-hidden">
                    <img
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex flex-wrap items-center gap-2 mb-3">
                      <span className="bg-[#F0F4ED] text-[#6B7F5C] text-xs font-medium px-2.5 py-1 rounded-full">
                        {post.category}
                      </span>
                      <span className="text-[#6B6B6B] text-xs">{post.date}</span>
                      <span className="text-[#6B6B6B] text-xs ml-auto">
                        {post.readTime} lectura
                      </span>
                    </div>
                    <h3 className="font-bold text-[#1A1A1A] text-base mb-2 leading-snug" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {post.title}
                    </h3>
                    <p className="text-[#6B6B6B] text-sm leading-relaxed line-clamp-2 mb-4">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5 text-xs text-[#6B6B6B]">
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-user-line" />
                        </div>
                        {post.author}
                      </div>
                      <Link
                        to="/blog"
                        className="flex items-center gap-1 text-[#6B7F5C] text-xs font-medium hover:gap-1.5 transition-all cursor-pointer"
                      >
                        Leer más
                        <div className="w-3 h-3 flex items-center justify-center">
                          <i className="ri-arrow-right-line" />
                        </div>
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>

            {filtered.length === 0 && (
              <div className="text-center py-16 text-[#6B6B6B]">No hay artículos en esta categoría todavía.</div>
            )}
          </div>
        </section>

        {/* Newsletter CTA */}
        <section className="py-16 bg-[#F0F4ED]">
          <div className="max-w-2xl mx-auto px-6 text-center">
            <div className="w-12 h-12 bg-[#6B7F5C]/15 rounded-full flex items-center justify-center mx-auto mb-4">
              <i className="ri-mail-send-line text-[#6B7F5C] text-xl" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold text-[#1A1A1A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              Recibe novedades en tu email
            </h2>
            <p className="text-[#6B6B6B] mb-6">Artículos mensuales sobre fiscalidad, laboral y seguros. Sin spam.</p>
            <Link
              to="/contacto"
              className="inline-flex items-center gap-2 bg-[#6B7F5C] text-white font-semibold px-7 py-3.5 rounded-lg hover:bg-[#5A6E4B] transition-colors cursor-pointer whitespace-nowrap"
            >
              Suscribirme al newsletter
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line" />
              </div>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <ChatWidget />
    </>
  );
}
